/**
 * THE CONSOLE'S ONE RING (Hamzah, 2026-10-20): any circle with a number inside, drawn by this and
 * nothing else. One size, one stroke, one clear space (`ring-geometry.ts`); the text on its own lines, the
 * number, its unit, then the qualifier smaller and grey; each line on one line, set smaller to fit
 * rather than wrapped (`fitFont`, measured after layout).
 *
 * What it can draw:
 *   fraction   one arc, a share of the whole (energy against a usual day, a fuel or charge level, a
 *              score), in a `tone` or a caller's own class where a page carries its own palette
 *   tick       a short mark across the stroke ("usual by now")
 *   segments   a donut's slices end to end, each with its own class and, where a slice is a control,
 *              its own handlers (`props`)
 * `label` is what a screen reader hears for the whole ring, since the lines are its picture.
 */
import { useLayoutEffect, useRef, useState, type ReactNode, type SVGProps } from 'react';
import { cn } from '@/lib/utils';
import { RING_C, RING_CENTRE, RING_R, RING_SIZE, RING_STROKE, RING_TEXT_W, RING_TYPE, arcDash, fitFont, ringLines, segmentsOf, tickAt } from './ring-geometry';
import './ring.css';

export type RingTone = 'chart' | 'pass' | 'warn' | 'fail';

export interface RingSegment {
  key: string;
  /** Its share of the whole: any non-negative weight, laid end to end with the others. */
  weight: number;
  className?: string;
  props?: SVGProps<SVGCircleElement>;
}

/** One line of the ring's text, set at its size or smaller to fit the ring's text box. */
function FitLine({ text, kind, className }: { text: string; kind: keyof typeof RING_TYPE; className?: string | undefined }): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const { base, min } = RING_TYPE[kind];
  const [size, setSize] = useState<number>(base);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* Measured at its own size, as an inline box that does not wrap: its natural width. */
    el.style.fontSize = `${base}px`;
    el.style.display = 'inline-block';
    const natural = el.scrollWidth;
    el.style.display = '';
    const next = fitFont(base, natural, RING_TEXT_W, min);
    el.style.fontSize = `${next}px`;
    setSize(next);
  }, [text, base, min]);
  return (
    /* Runtime size: the line's own, or smaller to fit. */
    <span ref={ref} className={cn('lm-ring-line', className)} style={{ fontSize: size }}>
      {text}
    </span>
  );
}

export function Ring({
  fraction,
  tone = 'chart',
  fillClassName,
  trackClassName,
  tick,
  tickClassName,
  segments,
  segmentGap = 0,
  value,
  unit,
  note,
  valueClassName,
  label,
  className,
}: {
  /** One arc's share of the circle, 0 to 1; null or 0 draws the track alone. */
  fraction?: number | null | undefined;
  tone?: RingTone;
  /** The fill's own class where a page carries its own palette (the Overview's `ov-ring-fill`). */
  fillClassName?: string | undefined;
  trackClassName?: string | undefined;
  /** A mark across the stroke at this share of the way round. */
  tick?: number | null | undefined;
  tickClassName?: string | undefined;
  /** A donut in place of one arc. */
  segments?: readonly RingSegment[] | undefined;
  /** A clear length after each slice, where there are two or more. */
  segmentGap?: number;
  /** The big number, already formatted; a node (a placeholder) is drawn as it is. */
  value: string | ReactNode;
  unit?: string | null | undefined;
  /** The time or the qualifier: "since 7:15 AM", "fuel", "partial". */
  note?: string | null | undefined;
  /** The number's ink where it carries a state (`ov-warn-ink`, a band). */
  valueClassName?: string | undefined;
  /** What the whole ring says, for a screen reader. */
  label: string;
  className?: string | undefined;
}): ReactNode {
  const dash = segments ? null : arcDash(fraction);
  const parts = segments ? segmentsOf(segments.map((s) => s.weight), segmentGap) : [];
  const t = tick === null || tick === undefined ? null : tickAt(tick);
  return (
    <div className={cn('relative flex-none', className)} style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} className="absolute inset-0 size-full" role="img" aria-label={label}>
        <circle cx={RING_CENTRE} cy={RING_CENTRE} r={RING_R} fill="none" strokeWidth={RING_STROKE} className={trackClassName ?? 'lm-ring-track'} />
        {dash !== null && (
          <circle
            cx={RING_CENTRE}
            cy={RING_CENTRE}
            r={RING_R}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={dash}
            transform={`rotate(-90 ${RING_CENTRE} ${RING_CENTRE})`}
            className={fillClassName ?? cn('lm-ring-fill', tone !== 'chart' && `is-${tone}`)}
          />
        )}
        {segments &&
          segments.map((s, i) => {
            const p = parts[i];
            if (!p || p.length <= 0) return null;
            return (
              <circle
                key={s.key}
                cx={RING_CENTRE}
                cy={RING_CENTRE}
                r={RING_R}
                fill="none"
                strokeWidth={RING_STROKE}
                strokeDasharray={`${p.length} ${RING_C - p.length}`}
                strokeDashoffset={-p.offset}
                transform={`rotate(-90 ${RING_CENTRE} ${RING_CENTRE})`}
                className={s.className}
                {...s.props}
              />
            );
          })}
        {t !== null && <line {...t} strokeWidth="2.5" strokeLinecap="round" className={tickClassName ?? 'lm-ring-tick'} />}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <div className="flex flex-col items-center leading-[1.15]" style={{ width: RING_TEXT_W }}>
          {typeof value !== 'string' && value}
          {ringLines({ value: typeof value === 'string' ? value : '', unit, note }).map((l) =>
            l.kind === 'value' ? (
              l.text !== '' && <FitLine key="value" text={l.text} kind="value" className={cn('lm-num font-semibold tracking-[-0.015em] text-[var(--fg)]', valueClassName)} />
            ) : (
              <FitLine key={l.kind} text={l.text} kind={l.kind} className={cn('text-[var(--muted-fg)]', l.kind === 'note' && 'mt-[1px]')} />
            ),
          )}
        </div>
      </div>
    </div>
  );
}
